import { useRef, useEffect, useState, useContext } from "react"
import { motion, useInView } from "framer-motion"
import { GatsbyImage, getImage, StaticImage } from "gatsby-plugin-image"
import useTranslations from "hooks/useTranslations"
import { graphql } from "gatsby"
import get from "lodash.get"
import { NavBar } from "components/organisms/materials/NavBar"
import { Lectures } from "components/organisms/materials/Lectures"
import { siteCountry } from "utils/env"
import OnboardingSection from "components/molecules/onboarding"
import { Container } from "components/atoms/materials/Container"
import { SectionItem } from "components/molecules/materials/SectionItem"
import { Curicculum } from "components/molecules/materials/Curicculum"
import Divided from "components/organisms/common/Divided"
import Button from "components/buttons/Button"
import Contact from "components/organisms/somebuddy/Contact"
import SubscribeForm from "components/SubscribeForm"
import { LocaleContext } from "contexts"
import SEO from "components/seo"
import i18nConfig from "../../../config/i18n"
import FormattedTranslation from "components/atoms/FormattedTranslation"

interface SectionProps {
  id: string
  title: string
  description?: string
  lectures: Lecture[]
  children?: React.ReactNode
  showContactButton?: boolean
}

const Section = ({
  id,
  title,
  description,
  lectures,
  children,
  showContactButton,
}: SectionProps) => {
  return (
    <section
      id={id}
      aria-labelledby={title}
      className="my-6 scroll-mt-14 pt-8 sm:my-8 sm:scroll-mt-14 sm:pt-10 lg:my-12 lg:scroll-mt-24 lg:pt-16"
    >
      <div className="space-y-10">
        <Container size="lg">
          <p className="mb-0 mt-8 font-display text-4xl font-bold tracking-tight text-slate-900">
            {title}
          </p>
          {description ? (
            <p className="mb-0 mt-4 text-lg tracking-tight text-slate-700">
              {description}
            </p>
          ) : null}
        </Container>
        {children}
        <Lectures
          lectures={lectures}
          sectionId={id}
          showContactButton={showContactButton}
        />
      </div>
    </section>
  )
}

const CTA = ({
  title,
  description,
  button1Text,
  link1,
  button2Text = "",
  link2 = "",
}) => (
  <div className="mt-20 bg-kkm-blue pb-12 pt-12">
    <Container size="lg">
      <div className="grid grid-cols-1 items-end gap-y-4 lg:grid-cols-5 ">
        <div className="col-span-2 lg:mr-8">
          <h2 className="m-0 whitespace-pre-line text-2xl font-extrabold tracking-tight text-kkm-yellow/90 md:text-3xl lg:text-left lg:text-5xl">
            {title}
          </h2>
        </div>
        <div className="col-span-2 flex h-full flex-col justify-between space-y-5 lg:space-y-3">
          <p className="mb-0 text-lg text-slate-50">{description}</p>
          <div className="inline-flex space-x-4">
            <Button link={link1} label={button1Text} bgStyle="yellow" />
            {button2Text ? (
              <Button link={link2} label={button2Text} bgStyle="yellow" />
            ) : null}
          </div>
        </div>
      </div>
    </Container>
  </div>
)

export default function SchoolMaterials({ data }) {
  const { locale } = useContext(LocaleContext)
  const countryOptions = i18nConfig[siteCountry]
  const localeOptions = countryOptions[locale]
  const showNewsletterSubscription = get(
    localeOptions,
    "showNewsletterSubscription",
  )
  const {
    materialsTitle,
    otherSectionVideo1Title,
    otherSectionVideo1Description,
    otherSectionVideo1Link,
    otherSectionVideo2Title,
    otherSectionVideo2Description,
    otherSectionVideo2Link,
    webinarHeroButton,
    materials1Title,
    materials2Title,
    materials3Title,
    materials4Title,
    materialsPdfLinkText,
    otherSectionMaterial1PdfLinkText,
    materials1Lecture1Title,
    materials1Lecture1Description,
    materials1Lecture1Video,
    materials1Lecture1Pdf,
    materials1Lecture2Title,
    materials1Lecture2Description,
    materials1Lecture2Video,
    materials1Lecture2Pdf,
    materials1Lecture3Title,
    materials1Lecture3Description,
    materials1Lecture3Video,
    materials1Lecture3Pdf,
    materials1Lecture4Title,
    materials1Lecture4Description,
    materials1Lecture4Video,
    materials1Lecture4Pdf,
    materials1Lecture5Title,
    materials1Lecture5Description,
    materials1Lecture5Video,
    materials1Lecture5Pdf,
    materials1Lecture6Title,
    materials1Lecture6Description,
    materials1Lecture6Video,
    materials1Lecture6Pdf,
    materials2Lecture1Title,
    materials2Lecture1Description,
    materials2Lecture1Video,
    materials2Lecture1Pdf,
    materials2Lecture2Title,
    materials2Lecture2Description,
    materials2Lecture2Video,
    materials2Lecture2Pdf,
    materials2Lecture3Title,
    materials2Lecture3Description,
    materials2Lecture3Video,
    materials2Lecture3Pdf,
    materials2Lecture4Title,
    materials2Lecture4Description,
    materials2Lecture4Video,
    materials2Lecture4Pdf,
    materials2Lecture5Title,
    materials2Lecture5Description,
    materials2Lecture5Video,
    materials2Lecture5Pdf,
    materials2Lecture6Title,
    materials2Lecture6Description,
    materials2Lecture6Video,
    materials2Lecture6Pdf,
    materials2Lecture7Title,
    materials2Lecture7Description,
    materials2Lecture7Video,
    materials2Lecture7Pdf,
    materials3Lecture4Video,
    materials3Lecture1Title,
    materials3Lecture1Description,
    materials3Lecture1Video,
    materials3Lecture1Pdf,
    materials3Lecture2Title,
    materials3Lecture2Description,
    materials3Lecture2Video,
    materials3Lecture2Pdf,
    materials3Lecture3Title,
    materials3Lecture3Description,
    materials3Lecture3Video,
    materials3Lecture3Pdf,
    materials3Lecture4Title,
    materials3Lecture4Description,
    materials3Lecture4Pdf,
    materials3Lecture5Title,
    materials3Lecture5Description,
    materials3Lecture5Video,
    materials3Lecture5Pdf,
    materials3Lecture6Title,
    materials3Lecture6Description,
    materials3Lecture6Video,
    materials3Lecture6Pdf,
    materials3Lecture7Title,
    materials3Lecture7Description,
    materials3Lecture7Video,
    materials3Lecture7Pdf,
    materials4Lecture3Pdf,
    materials4Lecture4Title,
    materials4Lecture4Description,
    materials4Lecture4Video,
    materials4Lecture4Pdf,
    materials4Lecture5Title,
    materials4Lecture5Description,
    materials4Lecture5Video,
    materials4Lecture5Pdf,
    materials4Lecture6Title,
    materials4Lecture6Description,
    materials4Lecture6Video,
    materials4Lecture6Pdf,
    materialsOtherTitle,
    otherSectionMaterial1Pdf,
    otherSectionMaterial1Title,
    otherSectionMaterial1Description,
    materialsDescription,
    materialsIntro1,
    materialsIntro2,
    materialsIntro3,
    materialsIntro4,
    materialsIntroLink,
    materialsCuricculumTitle,
    materialsCuricculum1,
    materialsCuricculum2,
    materialsCuricculum3,
    materialsCuricculum4,
    materialsCuricculum5,
    materialsCuricculum6,
    materialsCuricculum7,
    materialsCuricculum8,
    materialsCuricculum9,
    materialsCTA1Title,
    materialsCTA1Description,
  } = useTranslations()

  const ref = useRef(null)
  const isInView = useInView(ref)

  const [showSectionBoxes, setShowSectionBoxes] = useState(false)

  useEffect(() => {
    if (isInView) setShowSectionBoxes(true)
  }, [isInView])

  const HeroImage = ({ className }) => (
    <GatsbyImage
      alt="Trainings hero"
      loading="eager"
      image={getImage(data.schoolTrainingsHero)}
      className={className}
      imgStyle={{
        objectFit: "cover",
        objectPosition: "top",
      }}
    />
  )

  const CurriculumImage = () => (
    <GatsbyImage
      alt="Trainings hero"
      loading="eager"
      image={getImage(data.schoolCurriculum)}
      imgStyle={{
        objectFit: "cover",
        objectPosition: "top",
      }}
    />
  )

  const SchoolPhotosPdfFi = () => (
    <StaticImage
      src="../../images/schools/photo-schools-pdf-fi.webp"
      alt="photography in school materials cover"
    />
  )

  const SchoolPhotosPdfSv = () => (
    <StaticImage
      src="../../images/schools/photo-schools-pdf-sv.webp"
      alt="photography in school materials cover"
    />
  )

  const schoolPhotosCover = {
    "fi-fi": SchoolPhotosPdfFi,
    "sv-fi": SchoolPhotosPdfSv,
  }

  const sections = [
    {
      id: "grades-1-2",
      title: materials1Title,
      color: "persona-color-1",
      lectures: [
        {
          title: materials1Lecture1Title,
          description: materials1Lecture1Description,
          videoLink: materials1Lecture1Video,
          linkText: materialsPdfLinkText,
          link: materials1Lecture1Pdf,
        },
        {
          title: materials1Lecture2Title,
          description: materials1Lecture2Description,
          videoLink: materials1Lecture2Video,
          linkText: materialsPdfLinkText,
          link: materials1Lecture2Pdf,
        },
        {
          title: materials1Lecture3Title,
          description: materials1Lecture3Description,
          videoLink: materials1Lecture3Video,
          linkText: materialsPdfLinkText,
          link: materials1Lecture3Pdf,
        },
        {
          title: materials1Lecture4Title,
          description: materials1Lecture4Description,
          videoLink: materials1Lecture4Video,
          linkText: materialsPdfLinkText,
          link: materials1Lecture4Pdf,
        },
        {
          title: materials1Lecture5Title,
          description: materials1Lecture5Description,
          videoLink: materials1Lecture5Video,
          linkText: materialsPdfLinkText,
          link: materials1Lecture5Pdf,
        },
        {
          title: materials1Lecture6Title,
          description: materials1Lecture6Description,
          videoLink: materials1Lecture6Video,
          linkText: materialsPdfLinkText,
          link: materials1Lecture6Pdf,
        },
      ],
    },
    {
      id: "grades-3-6",
      title: materials2Title,
      lectures: [
        {
          title: materials2Lecture1Title,
          description: materials2Lecture1Description,
          videoLink: materials2Lecture1Video,
          linkText: materialsPdfLinkText,
          link: materials2Lecture1Pdf,
        },
        {
          title: materials2Lecture2Title,
          description: materials2Lecture2Description,
          videoLink: materials2Lecture2Video,
          linkText: materialsPdfLinkText,
          link: materials2Lecture2Pdf,
        },
        {
          title: materials2Lecture3Title,
          description: materials2Lecture3Description,
          videoLink: materials2Lecture3Video,
          linkText: materialsPdfLinkText,
          link: materials2Lecture3Pdf,
        },
        {
          title: materials2Lecture4Title,
          description: materials2Lecture4Description,
          videoLink: materials2Lecture4Video,
          linkText: materialsPdfLinkText,
          link: materials2Lecture4Pdf,
        },
        {
          title: materials2Lecture5Title,
          description: materials2Lecture5Description,
          videoLink: materials2Lecture5Video,
          linkText: materialsPdfLinkText,
          link: materials2Lecture5Pdf,
        },
        {
          title: materials2Lecture6Title,
          description: materials2Lecture6Description,
          videoLink: materials2Lecture6Video,
          linkText: materialsPdfLinkText,
          link: materials2Lecture6Pdf,
        },
        {
          title: materials2Lecture7Title,
          description: materials2Lecture7Description,
          videoLink: materials2Lecture7Video,
          linkText: materialsPdfLinkText,
          link: materials2Lecture7Pdf,
        },
      ],
      color: "persona-color-2",
    },
    {
      id: "grades-7-9",
      title: materials3Title,
      lectures: [
        {
          title: materials3Lecture1Title,
          description: materials3Lecture1Description,
          videoLink: materials3Lecture1Video,
          linkText: materialsPdfLinkText,
          link: materials3Lecture1Pdf,
        },
        {
          title: materials3Lecture2Title,
          description: materials3Lecture2Description,
          videoLink: materials3Lecture2Video,
          linkText: materialsPdfLinkText,
          link: materials3Lecture2Pdf,
        },
        {
          title: materials3Lecture3Title,
          description: materials3Lecture3Description,
          videoLink: materials3Lecture3Video,
          linkText: materialsPdfLinkText,
          link: materials3Lecture3Pdf,
        },
        {
          title: materials3Lecture4Title,
          description: materials3Lecture4Description,
          videoLink: materials3Lecture4Video,
          linkText: materialsPdfLinkText,
          link: materials3Lecture4Pdf,
        },
        {
          title: materials3Lecture5Title,
          description: materials3Lecture5Description,
          videoLink: materials3Lecture5Video,
          linkText: materialsPdfLinkText,
          link: materials3Lecture5Pdf,
        },
        {
          title: materials3Lecture6Title,
          description: materials3Lecture6Description,
          videoLink: materials3Lecture6Video,
          linkText: materialsPdfLinkText,
          link: materials3Lecture6Pdf,
        },
        {
          title: materials3Lecture7Title,
          description: materials3Lecture7Description,
          videoLink: materials3Lecture7Video,
          linkText: materialsPdfLinkText,
          link: materials3Lecture7Pdf,
        },
      ],
      color: "persona-color-3",
    },
    {
      id: "upper-secondary",
      title: materials4Title,
      description: "",
      color: "persona-color-4",
      lectures: [
        {
          title: materials3Lecture1Title,
          description: materials3Lecture1Description,
          videoLink: materials3Lecture1Video,
          linkText: materialsPdfLinkText,
          link: materials3Lecture1Pdf,
        },
        {
          title: materials3Lecture2Title,
          description: materials3Lecture2Description,
          videoLink: materials3Lecture2Video,
          linkText: materialsPdfLinkText,
          link: materials3Lecture2Pdf,
        },
        {
          title: materials3Lecture3Title,
          description: materials3Lecture3Description,
          videoLink: materials3Lecture3Video,
          linkText: materialsPdfLinkText,
          link: materials4Lecture3Pdf,
        },
        {
          title: materials3Lecture4Title,
          description: materials3Lecture4Description,
          videoLink: materials3Lecture4Video,
          linkText: materialsPdfLinkText,
          link: materials3Lecture4Pdf,
        },
        {
          title: materials4Lecture5Title,
          description: materials4Lecture5Description,
          videoLink: materials4Lecture5Video,
          linkText: materialsPdfLinkText,
          link: materials4Lecture5Pdf,
        },
        {
          title: materials4Lecture6Title,
          description: materials4Lecture6Description,
          videoLink: materials4Lecture6Video,
          linkText: materialsPdfLinkText,
          link: materials4Lecture6Pdf,
        },
      ],
    },
  ]

  const otherSection = {
    id: "other-videos",
    title: materialsOtherTitle,
    description: "",
    color: "kkm-blue",
    lectures: [
      {
        title: otherSectionVideo1Title,
        description: otherSectionVideo1Description,
        videoLink: otherSectionVideo1Link,
      },
      // {
      //   title: otherSectionVideo2Title,
      //   description: otherSectionVideo2Description,
      //   videoLink: otherSectionVideo2Link,
      // },
      {
        title: otherSectionMaterial1Title,
        link: otherSectionMaterial1Pdf,
        linkText: otherSectionMaterial1PdfLinkText,
        description: otherSectionMaterial1Description,
        Cover: schoolPhotosCover[locale],
      },
    ],
  }

  const variants = {
    open: {
      opacity: 1,
      transition: { staggerChildren: 0.07, delayChildren: 0.5 },
    },
    hidden: {
      opacity: 0,
      transition: { staggerChildren: 0.05, staggerDirection: -1 },
    },
  }

  return (
    <>
      <SEO title={materialsTitle} />
      <Divided
        Image={HeroImage}
        title={materialsTitle}
        theme="yellow"
        className="mt-20 py-20"
        noVerticalPadding
      >
        <p className="m-0 whitespace-pre-line text-left font-display text-2xl text-zinc-800 sm:text-2xl">
          {materialsDescription}
        </p>
      </Divided>
      <Container
        size="lg"
        className="mb-10 mt-20 flex flex-row items-start lg:mb-16"
      >
        <motion.ul
          variants={variants}
          className="hidden pr-10 sm:block"
          animate={showSectionBoxes ? "open" : "hidden"}
          ref={ref}
          initial="hidden"
        >
          {[
            ...sections.map((s) => ({
              id: s.id,
              title: s.title,
              color: s.color,
            })),
            {
              id: otherSection.id,
              title: otherSection.title,
              color: otherSection.color || "persona-color-default",
            },
          ].map((s) => (
            <SectionItem key={s.id} title={s.title} id={s.id} color={s.color} />
          ))}
        </motion.ul>
        <div className="prose md:max-w-2xl">
          <p>{materialsIntro1}</p>
          <p>{materialsIntro2}</p>
          <p>
            {materialsIntro3}
            <a href="#contact">{materialsIntroLink}</a>
            {materialsIntro4}.
          </p>
        </div>
      </Container>
      {siteCountry !== "se" && (
        <Curicculum title={materialsCuricculumTitle} image={CurriculumImage}>
          <div className="prose text-slate-900">
            <p className="">{materialsCuricculum1}</p>
            <p className="font-bold">{materialsCuricculum2}</p>
            <ul className="list-disc text-sm ">
              {[
                materialsCuricculum3,
                materialsCuricculum4,
                materialsCuricculum5,
                materialsCuricculum6,
                materialsCuricculum7,
                materialsCuricculum8,
                materialsCuricculum9,
              ].map((el) => (
                <li>
                  <FormattedTranslation html={el} />
                </li>
              ))}
            </ul>
          </div>
        </Curicculum>
      )}
      {(() => {
        const sectionsWithFlags = sections.map((s) => ({
          ...s,
          showContactButton:
            siteCountry === "se" && s.id !== "grades-1-2" ? true : false,
        }))

        const navItemsBase = sectionsWithFlags.map((s) => ({
          id: s.id,
          title: s.title,
        }))
        const otherSectionNavItem = {
          id: otherSection.id,
          title: otherSection.title,
        }

        const navSections =
          siteCountry === "se"
            ? navItemsBase
            : sections
                .map((s) => ({ id: s.id, title: s.title }))
                .concat(otherSectionNavItem)

        return (
          <>
            <NavBar sections={navSections} />
            {sectionsWithFlags.map((section) => (
              <Section
                key={section.id}
                id={section.id}
                title={section.title}
                description={section.description}
                lectures={section.lectures}
                showContactButton={section.showContactButton}
              />
            ))}
            {siteCountry !== "se" && (
              <Section
                id={otherSection.id}
                title={otherSection.title}
                description={otherSection.description}
                lectures={otherSection.lectures}
              >
                <OnboardingSection
                  id="onboarding-video"
                  title={otherSectionVideo2Title}
                  descriptions={[otherSectionVideo2Description]}
                  videoLink={`https://www.youtube-nocookie.com/embed/${otherSectionVideo2Link}`}
                  theme="blue"
                  size="small"
                  buttonAction="openVideo"
                />
              </Section>
            )}
            {showNewsletterSubscription ? (
              <SubscribeForm segment="schools" />
            ) : null}
            <Contact />
          </>
        )
      })()}
    </>
  )
}

export const pageQuery = graphql`
  query {
    schoolTrainingsHero: file(
      relativePath: { eq: "schools/pexels-fox-1595391.jpeg" }
    ) {
      childImageSharp {
        gatsbyImageData(quality: 90)
      }
    }
    schoolCurriculum: file(
      relativePath: { eq: "schools/pexels-fauxels-3184638.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(quality: 90)
      }
    }
  }
`
